import { useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Link } from "react-router-dom";
import Lenis from "lenis";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import StickyCallBar from "./components/StickyCallBar";
import { initGA as initGoogleAnalytics, trackPageView as trackPageViewGA } from "./lib/ga";
import Home from "./pages/Home";
import ServicePage from "./pages/ServicePage";
import CityPage from "./pages/CityPage";
import ZonesPage from "./pages/ZonesPage";
import GaleriePage from "./pages/GaleriePage";
import AvisPage from "./pages/AvisPage";
import AProposPage from "./pages/AProposPage";
import DevisPage from "./pages/DevisPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import MentionsLegales from "./pages/MentionsLegales";
import PlanMarketingPage from "./pages/PlanMarketingPage";
import NotFound from "./pages/NotFound";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-ink text-ivory flex flex-col items-center justify-center gap-6 p-8 text-center">
                    <h1 className="text-3xl font-display italic text-gold">Une erreur est survenue</h1>
                    <p className="text-smoke">Revenez à l'accueil pour continuer votre visite.</p>
                    <Link to="/" className="rounded-full bg-gold text-ink font-bold px-8 py-3" data-testid="error-back-home">
                        Retour à l'accueil
                    </Link>
                </div>
            );
        }
        return this.props.children;
    }
}

function SlugRoute() {
    const { slug } = useParams();
    if (slug.startsWith("peintre-")) return <CityPage />;
    return <ServicePage />;
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
        trackPageViewGA(pathname);
    }, [pathname]);
    return null;
}

function LenisProvider({ children }) {
    useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced) return undefined;
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);
    return children;
}

export default function App() {
    useEffect(() => {
        initGoogleAnalytics();
    }, []);
    return (
        <BrowserRouter>
            <ScrollToTop />
            <ErrorBoundary>
                <LenisProvider>
                    <div className="min-h-screen bg-ink text-ivory font-sans flex flex-col">
                        <div className="grain" aria-hidden="true" />
                        <Navbar />
                        <main className="flex-1 pb-14 sm:pb-0">
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/devis" element={<DevisPage />} />
                                <Route path="/galerie" element={<GaleriePage />} />
                                <Route path="/avis" element={<AvisPage />} />
                                <Route path="/a-propos" element={<AProposPage />} />
                                <Route path="/zones" element={<ZonesPage />} />
                                <Route path="/blog" element={<BlogPage />} />
                                <Route path="/blog/:slug" element={<BlogPostPage />} />
                                <Route path="/mentions-legales" element={<MentionsLegales />} />
                                <Route path="/plan-marketing" element={<PlanMarketingPage />} />
                                <Route path="/:slug" element={<SlugRoute />} />
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </main>
                        <Footer />
                        <StickyCallBar />
                    </div>
                </LenisProvider>
            </ErrorBoundary>
        </BrowserRouter>
    );
}
